import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Paper,
  Card,
  CardMedia,
  ImageList,
  ImageListItem,
  Tooltip,
  Stack,
  Modal,
} from '@mui/material';
import {
  Close,
  Home,
  LocationOn,
  AttachMoney,
  Straighten,
  Bed,
  Bathtub,
  Info,
  Person,
  CalendarToday,
  CheckCircle,
  Warning,
  Cancel,
  ZoomIn,
  NavigateNext,
  NavigateBefore,
} from '@mui/icons-material';
import { Property, PropertyStatus } from '../../types';

interface PropertyDetailsDialogProps {
  open: boolean;
  property: Property | null;
  onClose: () => void;
}

const PropertyDetailsDialog: React.FC<PropertyDetailsDialogProps> = ({
  open,
  property,
  onClose,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  
  if (!property) return null;

  const handleOpenImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleCloseImage = () => {
    setSelectedImageIndex(null);
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null && property.images) {
      setSelectedImageIndex((selectedImageIndex + 1) % property.images.length);
    }
  };

  const handlePrevImage = () => {
    if (selectedImageIndex !== null && property.images) {
      setSelectedImageIndex((selectedImageIndex - 1 + property.images.length) % property.images.length);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return { bg: '#e8f5e9', color: '#2e7d32', icon: <CheckCircle /> };
      case 'PENDING':
        return { bg: '#fff3e0', color: '#e65100', icon: <Warning /> };
      case 'REJECTED':
        return { bg: '#ffebee', color: '#c62828', icon: <Cancel /> };
      case 'SOLD':
        return { bg: '#e1f5fe', color: '#01579b', icon: <CheckCircle /> };
      case 'RENTED':
        return { bg: '#f3e5f5', color: '#6a1b9a', icon: <CheckCircle /> };
      default:
        return { bg: '#f5f5f5', color: '#616161', icon: null };
    }
  };

  const statusInfo = getStatusColor(property.status);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1.5,
          borderBottom: '1px solid #eee',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#1a1a1a' }}>
            Property Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Complete information about this property
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Main Property Title and Status */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  color: '#1a1a1a',
                  mb: 1,
                  wordBreak: 'break-word',
                }}
              >
                {property.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOn sx={{ fontSize: '1.3rem', color: '#1967d2' }} />
                <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
                  {property.address}, {property.city}, {property.country}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Status Badge */}
          {statusInfo.icon ? (
            <Chip
              icon={statusInfo.icon}
              label={property.status}
              sx={{
                backgroundColor: statusInfo.bg,
                color: statusInfo.color,
                fontWeight: 700,
                fontSize: '0.9rem',
                px: 1,
                height: 32,
              }}
            />
          ) : (
            <Chip
              label={property.status}
              sx={{
                backgroundColor: statusInfo.bg,
                color: statusInfo.color,
                fontWeight: 700,
                fontSize: '0.9rem',
                px: 1,
                height: 32,
              }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Images Section */}
        {property.images && property.images.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Home sx={{ color: '#1967d2' }} />
              Gallery ({property.images.length} {property.images.length === 1 ? 'image' : 'images'})
            </Typography>
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '2px solid #e8eef5',
              }}
            >
              {property.images.length === 1 ? (
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    height: 350,
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                  onClick={() => handleOpenImage(0)}
                >
                  <CardMedia
                    component="img"
                    image={property.images[0]}
                    alt={property.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'backgroundColor 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      },
                    }}
                  >
                    <ZoomIn sx={{ fontSize: '3rem', color: 'white', opacity: 0 }} />
                  </Box>
                </Card>
              ) : (
                <ImageList sx={{ width: '100%' }} cols={3} rowHeight={200} gap={16}>
                  {property.images.map((image, index) => (
                    <ImageListItem
                      key={index}
                      onClick={() => handleOpenImage(index)}
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover .zoom-icon': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Card
                        sx={{
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          height: '100%',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.03)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={image}
                          alt={`${property.title} ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <Box
                          className="zoom-icon"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          }}
                        >
                          <ZoomIn sx={{ fontSize: '2rem', color: 'white' }} />
                        </Box>
                      </Card>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {index + 1} / {property.images.length}
                      </Box>
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Paper>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Key Features Section */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#f9f9f9',
                borderRadius: 1.5,
                border: '1px solid #eee',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AttachMoney sx={{ color: '#1967d2', fontSize: '1.5rem' }} />
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Price
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#1967d2' }}>
                {formatCurrency(property.price)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#f9f9f9',
                borderRadius: 1.5,
                border: '1px solid #eee',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Home sx={{ color: '#1967d2', fontSize: '1.5rem' }} />
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Property Type
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#333' }}>
                {property.propertyType}
              </Typography>
            </Paper>
          </Grid>

          {property.size && (
            <Grid item xs={6} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Straighten sx={{ color: '#1967d2', fontSize: '1.5rem' }} />
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                    Size
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#333' }}>
                  {property.size} m²
                </Typography>
              </Paper>
            </Grid>
          )}

          {property.bedrooms !== undefined && (
            <Grid item xs={6} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Bed sx={{ color: '#1967d2', fontSize: '1.5rem' }} />
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                    Bedrooms
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#333' }}>
                  {property.bedrooms || '—'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {property.bathrooms !== undefined && (
            <Grid item xs={6} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Bathtub sx={{ color: '#1967d2', fontSize: '1.5rem' }} />
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                    Bathrooms
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#333' }}>
                  {property.bathrooms || '—'}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Description Section */}
        {property.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info sx={{ color: '#1967d2' }} />
                Description
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#fafafa',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {property.description}
                </Typography>
              </Paper>
            </Box>
          </>
        )}

        {/* Features Section */}
        {property.features && property.features.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: '#1967d2' }} />
                Features
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {property.features.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature}
                    variant="outlined"
                    sx={{
                      backgroundColor: '#f0f7ff',
                      borderColor: '#1967d2',
                      color: '#1967d2',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {/* Agent Information */}
        {property.agent && property.agent.user && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ color: '#1967d2' }} />
                Agent Information
              </Typography>
              <Paper
                sx={{
                  p: 2.5,
                  backgroundColor: '#f0f7ff',
                  borderRadius: 1.5,
                  border: '2px solid #1967d2',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                      {property.agent.user.firstName} {property.agent.user.lastName}
                    </Typography>
                  </Box>
                  {property.agent.user.email && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1967d2', wordBreak: 'break-all' }}>
                        {property.agent.user.email}
                      </Typography>
                    </Box>
                  )}
                  {property.agent.user.phone && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                        {property.agent.user.phone}
                      </Typography>
                    </Box>
                  )}
                  {property.agent.rating && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>
                        Rating
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 700 }}>
                        ⭐ {property.agent.rating.toFixed(1)} / 5.0
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Box>
          </>
        )}

        {/* Rejection Reason */}
        {property.status === PropertyStatus.REJECTED && property.rejectionReason && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#c62828',
                }}
              >
                <Cancel sx={{ color: '#c62828' }} />
                Rejection Reason
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#ffebee',
                  borderRadius: 1.5,
                  border: '1px solid #ef5350',
                }}
              >
                <Typography variant="body2" sx={{ color: '#c62828', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {property.rejectionReason}
                </Typography>
              </Paper>
            </Box>
          </>
        )}

        {/* Dates Section */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday sx={{ color: '#1967d2' }} />
            Timeline
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Created
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#333', mt: 0.5 }}>
                  {formatDate(property.createdAt)}
                </Typography>
                {property.createdBy && (
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                    by {property.createdBy.firstName} {property.createdBy.lastName}
                  </Typography>
                )}
              </Paper>
            </Grid>

            {property.approvedAt && (
              <Grid item xs={6} sm={6}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 1.5,
                    border: '1px solid #81c784',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, textTransform: 'uppercase' }}>
                    Approved
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#1b5e20', mt: 0.5 }}>
                    {formatDate(property.approvedAt)}
                  </Typography>
                  {property.approvedBy && (
                    <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mt: 0.5 }}>
                      by {property.approvedBy.firstName} {property.approvedBy.lastName}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}

            <Grid item xs={6} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Last Updated
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#333', mt: 0.5 }}>
                  {formatDate(property.updatedAt)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1.5,
                  border: '1px solid #eee',
                }}
              >
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Property ID
                </Typography>
                <Tooltip title="Click to copy" arrow>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      display: 'block',
                      mt: 0.5,
                      fontFamily: 'monospace',
                      cursor: 'pointer',
                      '&:hover': { color: '#1967d2' },
                    }}
                    onClick={() => navigator.clipboard.writeText(property.id)}
                  >
                    {property.id.substring(0, 12)}...
                  </Typography>
                </Tooltip>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #eee',
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>

      {/* Image Lightbox Modal */}
      <Modal
        open={selectedImageIndex !== null}
        onClose={handleCloseImage}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Main Image */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'hidden',
              borderRadius: 2,
            }}
          >
            <CardMedia
              component="img"
              image={
                selectedImageIndex !== null && property.images
                  ? property.images[selectedImageIndex]
                  : ''
              }
              alt="Full view"
              sx={{
                maxWidth: '100%',
                maxHeight: '85vh',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />

            {/* Close Button */}
            <IconButton
              onClick={handleCloseImage}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                },
                zIndex: 10,
              }}
            >
              <Close sx={{ fontSize: '1.8rem' }} />
            </IconButton>

            {/* Navigation Buttons */}
            {property.images && property.images.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    },
                    zIndex: 10,
                  }}
                >
                  <NavigateBefore sx={{ fontSize: '1.8rem' }} />
                </IconButton>

                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    },
                    zIndex: 10,
                  }}
                >
                  <NavigateNext sx={{ fontSize: '1.8rem' }} />
                </IconButton>
              </>
            )}
          </Box>

          {/* Image Counter and Info */}
          {property.images && property.images.length > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                mt: 2,
                color: 'white',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {(selectedImageIndex ?? 0) + 1} / {property.images.length}
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </Dialog>
  );
};

export default PropertyDetailsDialog;
